import React from 'react';
import { CONTENT_TYPE } from '../../../../constants/gaEnums';
import { Typography, Box, Stack, Divider } from '@mui/material';
import AlbumTitleLink from '../../../../components/common/AlbumTitleLink';
import ArtistLink from '../../../../components/common/ArtistLink';
import BookmarkButton from '../../../../components/button/BookmarkButton';
import StarButton from '../../../../components/button/StarButton';
import RatingReadOnly from '../../../../components/common/RatingReadOnly';
import ReviewCount from '../../../../components/common/ReviewCount';
import BookmarkCount from '../../../../components/common/BookmarkCount';
import theme from '../../../../theme';
import ShareButton from '../../../../components/button/ShareButton';
import { albumSharePath } from '../../../../utils/albumSharePath';
import AppleMusicButton from '../../../../components/button/AppleMusicButton';
import SpotifyButton from '../../../../components/button/SpotifyButton';
import HomeAlbumFeatureButton from '../../../../components/button/HomeAlbumFeatureButton';
import RecommendedAlbumCommentButton from '../../../../components/button/RecommendedAlbumCommentButton';
import LatestAlbumManagementButton from '../../../../components/button/LatestAlbumManagementButton';

const AlbumInfoSection = ({
  albumInfo,
  isAdmin,
  onBookmarkToggle,
  onStarredToggle,
  onHomeLatestToggle,
  onHomeRecommendedToggle,
  onRecommendedCommentUpdate,
  onLatestAlbumRegionMove,
  onLatestAlbumHide,
  onLatestAlbumRegister,
  onRecommendedAlbumRemove,
  pageType,
}) => {
  if (!albumInfo) {
    return null;
  }

  return (
    <Box>
      {/* 앨범명 */}
      <AlbumTitleLink
        title={albumInfo.title}
        contentRating={albumInfo.contentRating}
        lineClamp={'unset'}
        sx={{
          fontSize: { xs: 16, md: 22, lg: 24 },
          mb: { xs: '8px', md: '12px', lg: '16px' },
          fontWeight: 700,
        }}
        iconSx={{
          width: { xs: '14px', md: '16px' },
          height: { xs: '14px', md: '16px' },
          verticalAlign: { xs: '-0.12em', md: '-0.02em' },
        }}
      />

      {/* 가수 */}
      <Box mb={{ xs: '24px', md: '24px', lg: '30px' }}>
        <ArtistLink
          artistName={albumInfo.artist.name}
          artistSource={albumInfo.artist.artistSource}
          artistSourceId={albumInfo.artist.sourceId}
          artistSlug={albumInfo.artist.slug}
          artistDbId={albumInfo.artist.id}
          sx={{
            fontSize: { xs: 14, md: 16 },
          }}
        />
      </Box>

      {/* 발매일 */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mb={{ xs: '16px', md: '16px', lg: '20px' }}
      >
        <Typography
          color={theme.palette.black[4]}
          fontSize={{ xs: 14, lg: 16 }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          발매일
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '8px',
            alignSelf: 'center',
            backgroundColor: theme.palette.grey.line,
          }}
        />
        <Typography
          color={theme.palette.black[4]}
          fontSize={{ xs: 14, lg: 16 }}
          sx={{ whiteSpace: 'nowrap', lineHeight: 1.5 }}
        >
          {albumInfo.releaseDate}
        </Typography>
      </Stack>

      {/* 레이블 */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mb={{ xs: '16px', md: '16px', lg: '20px' }}
      >
        <Typography
          color={theme.palette.black[4]}
          fontSize={{ xs: 14, lg: 16 }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          레이블
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '8px',
            alignSelf: 'center',
            backgroundColor: theme.palette.grey.line,
          }}
        />
        <Typography
          color={theme.palette.black[4]}
          fontSize={{ xs: 14, lg: 16 }}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.5,
          }}
        >
          {albumInfo.recordLabel}
        </Typography>
      </Stack>

      {/* 장르 */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mb={{ xs: '16px', md: '16px', lg: '20px' }}
      >
        <Typography
          color={theme.palette.black[4]}
          fontSize={{ xs: 14, lg: 16 }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          장르
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '8px',
            alignSelf: 'center',
            backgroundColor: theme.palette.grey.line,
          }}
        />
        <Typography
          color={theme.palette.black[4]}
          fontSize={{ xs: 14, lg: 16 }}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.5,
          }}
        >
          {albumInfo.genres.join(', ')}
        </Typography>
      </Stack>

      {/* 별점 / 리뷰 / 북마크 */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        mb={{ xs: '16px', md: '16px', lg: '30px' }}
      >
        <RatingReadOnly value={albumInfo.averageRating} />
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '8px',
            alignSelf: 'center',
            backgroundColor: theme.palette.grey.line,
          }}
        />
        <ReviewCount variant="detail" reviewCount={albumInfo.reviewCount} />
        <BookmarkCount variant="detail" bookmarkCount={albumInfo.bookmarkCount} />
      </Stack>

      {/* 공유 / 북마크 / 애플뮤직 열기 */}
      <Stack direction="row" spacing={1} alignItems="center">
        <ShareButton
          url={albumSharePath(albumInfo)}
          contentType={CONTENT_TYPE.ALBUM}
          contentId={albumInfo.sourceId}
          contentName={albumInfo.title}
          shareTitle={`${albumInfo.artist.name} — ${albumInfo.title}`}
          shareText={`${albumInfo.artist.name} — ${albumInfo.title}`}
          pageType={pageType}
        />
        <BookmarkButton isBookmarked={albumInfo.isBookmarked} onToggle={onBookmarkToggle} />
        {isAdmin && <StarButton isStarred={albumInfo.isStarred} onToggle={onStarredToggle} />}
        {isAdmin && (
          <HomeAlbumFeatureButton
            albumInfo={albumInfo}
            onToggleLatest={onHomeLatestToggle}
            onToggleRecommended={onHomeRecommendedToggle}
          />
        )}
        {isAdmin && (
          <RecommendedAlbumCommentButton
            albumInfo={albumInfo}
            onUpdate={onRecommendedCommentUpdate}
          />
        )}
        {isAdmin && (
          <LatestAlbumManagementButton
            albumInfo={albumInfo}
            onMoveRegion={onLatestAlbumRegionMove}
            onHide={onLatestAlbumHide}
            onRegister={onLatestAlbumRegister}
            onRemoveRecommended={onRecommendedAlbumRemove}
          />
        )}
        <AppleMusicButton
          url={
            albumInfo.urls?.apple_music || `https://music.apple.com/kr/album/${albumInfo.sourceId}`
          }
          contentId={albumInfo.id}
          pageType={pageType}
        />
        <SpotifyButton url={albumInfo.urls?.spotify} contentId={albumInfo.id} pageType={pageType} />
      </Stack>
    </Box>
  );
};

export default AlbumInfoSection;
